<template>
  <div class="space-y-4">
    <!-- Identity -->
    <div class="grid grid-cols-2 gap-4">
      <div>
        <label class="block text-xs font-medium text-gray-500 mb-1">Table name</label>
        <input
          :value="modelValue.table_name"
          :disabled="!editable || !isNew"
          type="text"
          class="w-full border border-gray-200 rounded px-2 py-1.5 text-sm font-mono disabled:bg-gray-50 disabled:text-gray-500 focus:outline-none focus:border-gray-400"
          @input="update('table_name', $event.target.value)"
        />
      </div>
      <div>
        <label class="block text-xs font-medium text-gray-500 mb-1">Snowflake schema</label>
        <!-- A free-text box let users send a schema they cannot write to (the
             old 'PUBLIC' default), which failed only at save time. The valid
             targets are a short fixed list, so offer exactly those. -->
        <select
          v-if="isNew && editable"
          :value="modelValue.snowflake_schema"
          class="w-full border border-gray-200 rounded px-2 py-1.5 text-sm font-mono focus:outline-none focus:border-gray-400"
          @change="update('snowflake_schema', $event.target.value)"
        >
          <option v-for="s in SNOWFLAKE_SCHEMAS" :key="s" :value="s">{{ s }}</option>
        </select>
        <input
          v-else
          :value="modelValue.snowflake_schema"
          disabled
          type="text"
          class="w-full border border-gray-200 rounded px-2 py-1.5 text-sm font-mono disabled:bg-gray-50 disabled:text-gray-500 focus:outline-none focus:border-gray-400"
        />
      </div>
      <div>
        <label class="block text-xs font-medium text-gray-500 mb-1">Sheet type</label>
        <select
          :value="modelValue.sheet_type"
          :disabled="!editable"
          class="w-full border border-gray-200 rounded px-2 py-1.5 text-sm disabled:bg-gray-50 disabled:text-gray-500 focus:outline-none focus:border-gray-400"
          @change="update('sheet_type', $event.target.value)"
        >
          <option v-for="(label, value) in SHEET_TYPES" :key="value" :value="value">{{ label }}</option>
        </select>
      </div>
      <div>
        <label class="block text-xs font-medium text-gray-500 mb-1">Description</label>
        <input
          :value="modelValue.description"
          :disabled="!editable"
          type="text"
          class="w-full border border-gray-200 rounded px-2 py-1.5 text-sm disabled:bg-gray-50 disabled:text-gray-500 focus:outline-none focus:border-gray-400"
          @input="update('description', $event.target.value)"
        />
      </div>
    </div>

    <label class="inline-flex items-center gap-2 text-sm text-gray-700">
      <input
        type="checkbox"
        :checked="modelValue.dont_parse"
        :disabled="!editable"
        @change="update('dont_parse', $event.target.checked)"
      />
      Don't parse (register only — auto-parse skips this schema)
    </label>

    <!-- Filename filters -->
    <div>
      <div class="flex items-center gap-2 mb-1">
        <label class="text-xs font-medium text-gray-500">Filename filters</label>
        <span class="text-xs text-gray-400">— required for auto-parse; a file matching any filter is routed to this schema</span>
      </div>
      <StringListEditor
        :items="filenameFilterStrings"
        :editable="editable"
        placeholder="e.g. PHARMACY"
        empty-text="No filters — this schema will never auto-parse."
        @update="setFilenameFilters"
      />
      <p v-if="preservedFilterCount" class="text-xs text-amber-600 mt-1">
        {{ preservedFilterCount }} advanced (non-text) filter{{ preservedFilterCount > 1 ? 's' : '' }} not shown here — preserved on save.
      </p>
    </div>

    <!-- Target sheet names -->
    <div>
      <div class="flex items-center gap-2 mb-1">
        <label class="text-xs font-medium text-gray-500">Target sheet names</label>
        <span class="text-xs text-gray-400">— optional; restricts parsing to these sheets</span>
      </div>
      <StringListEditor
        :items="modelValue.target_sheet_names || []"
        :editable="editable"
        placeholder="e.g. Claims Detail"
        empty-text="All sheets considered."
        @update="update('target_sheet_names', $event)"
      />
    </div>

    <!-- Columns -->
    <div>
      <div class="flex items-center justify-between mb-1">
        <label class="text-xs font-medium text-gray-500">
          Columns <span class="text-gray-400 font-normal">({{ (modelValue.columns || []).length }})</span>
        </label>
        <button
          v-if="editable"
          class="text-xs text-primary-500 hover:text-primary-600"
          @click="addColumn"
        >
          + Add column
        </button>
      </div>
      <div class="border border-gray-100 rounded max-h-80 overflow-y-auto">
        <table class="w-full text-xs">
          <thead class="sticky top-0 bg-gray-50">
            <tr class="text-left text-gray-500">
              <th class="py-1.5 px-2 font-medium">Name</th>
              <th class="py-1.5 px-2 font-medium w-36">Type</th>
              <th class="py-1.5 px-2 font-medium w-16">Nullable</th>
              <th class="py-1.5 px-2 font-medium w-12">PK</th>
              <th v-if="editable" class="w-8"></th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(col, i) in modelValue.columns"
              :key="i"
              class="border-t border-gray-50"
            >
              <td class="py-1 px-2">
                <input
                  v-if="editable"
                  :value="col.name"
                  type="text"
                  class="w-full border border-gray-200 rounded px-1.5 py-0.5 font-mono focus:outline-none focus:border-gray-400"
                  @input="updateColumn(i, 'name', $event.target.value)"
                />
                <span v-else class="font-mono text-gray-700">{{ col.name }}</span>
              </td>
              <td class="py-1 px-2">
                <select
                  v-if="editable"
                  :value="col.snowflake_type"
                  class="w-full border border-gray-200 rounded px-1 py-0.5 focus:outline-none focus:border-gray-400"
                  @change="updateColumn(i, 'snowflake_type', $event.target.value)"
                >
                  <option v-for="t in SNOWFLAKE_TYPES" :key="t" :value="t">{{ t }}</option>
                </select>
                <span v-else class="text-gray-500">{{ col.snowflake_type }}</span>
              </td>
              <td class="py-1 px-2">
                <input
                  type="checkbox"
                  :checked="col.nullable !== false"
                  :disabled="!editable"
                  @change="updateColumn(i, 'nullable', $event.target.checked)"
                />
              </td>
              <td class="py-1 px-2">
                <input
                  type="checkbox"
                  :checked="(modelValue.primary_key_columns || []).includes(col.name)"
                  :disabled="!editable"
                  @change="togglePrimaryKey(col.name, $event.target.checked)"
                />
              </td>
              <td v-if="editable" class="py-1 px-2 text-right">
                <button class="text-gray-300 hover:text-red-500" @click="removeColumn(i)">×</button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script>
import { SHEET_TYPES } from '@/utils/etlFormat';
import StringListEditor from './StringListEditor.vue';

/**
 * Pure controlled schema editor — shared by SchemaDetail (edit) and
 * DiscoverWizard (create). No HTTP; parent owns the object and saving.
 *
 * filename_filter envelope: the backend stores
 *   { filters: [{ filter_type: 'simple_string', value }], match_mode: 'any' }.
 * We edit only simple_string filters and PRESERVE any other filter types on
 * write (the old modal silently deleted them on save).
 */
export default {
  name: 'SchemaForm',
  components: { StringListEditor },
  props: {
    modelValue: { type: Object, required: true },
    editable: { type: Boolean, default: false },
    isNew: { type: Boolean, default: false },
  },
  emits: ['update:modelValue'],
  data() {
    return {
      SHEET_TYPES,
      SNOWFLAKE_TYPES: ['VARCHAR', 'INTEGER', 'FLOAT', 'DATE', 'TIMESTAMP_NTZ', 'BOOLEAN', 'VARIANT'],
      // Must stay in step with INGEST_SNOWFLAKE_SCHEMAS in api/etl_module/views.py.
      SNOWFLAKE_SCHEMAS: [
        'SOURCE_DATA_PRIVATE_PAYER',
        'SOURCE_DATA_CUSTOM_REPORTS',
        'SOURCE_DATA_CUSTOM_ATTRIBUTION',
      ],
    };
  },
  computed: {
    allFilters() {
      return this.modelValue.filename_filter?.filters || [];
    },
    filenameFilterStrings() {
      return this.allFilters
        .filter((f) => f.filter_type === 'simple_string')
        .map((f) => f.value);
    },
    preservedFilterCount() {
      return this.allFilters.filter((f) => f.filter_type !== 'simple_string').length;
    },
  },
  methods: {
    update(field, value) {
      this.$emit('update:modelValue', { ...this.modelValue, [field]: value });
    },
    setFilenameFilters(strings) {
      const preserved = this.allFilters.filter((f) => f.filter_type !== 'simple_string');
      const simple = strings.map((value) => ({ filter_type: 'simple_string', value }));
      const filters = [...preserved, ...simple];
      this.update('filename_filter', filters.length
        ? { filters, match_mode: this.modelValue.filename_filter?.match_mode || 'any' }
        : null);
    },
    updateColumn(index, field, value) {
      const columns = this.modelValue.columns.map((c, i) => (i === index ? { ...c, [field]: value } : c));
      this.update('columns', columns);
    },
    addColumn() {
      this.update('columns', [
        ...(this.modelValue.columns || []),
        { name: '', snowflake_type: 'VARCHAR', nullable: true },
      ]);
    },
    removeColumn(index) {
      const removed = this.modelValue.columns[index];
      const columns = this.modelValue.columns.filter((_, i) => i !== index);
      const pk = (this.modelValue.primary_key_columns || []).filter((n) => n !== removed.name);
      this.$emit('update:modelValue', { ...this.modelValue, columns, primary_key_columns: pk });
    },
    togglePrimaryKey(name, checked) {
      const current = this.modelValue.primary_key_columns || [];
      const pk = checked ? [...current, name] : current.filter((n) => n !== name);
      this.update('primary_key_columns', pk);
    },
  },
};
</script>
